import { AlertIconXs } from '@lichtblick/icons/svg/alert/alert-xs';
import classNames from 'classnames';
import type { FC, ReactElement } from 'react';
import { Checkbox as CheckboxAtom } from '../../atoms/checkbox';
import { FieldFooter } from '../../atoms/field-footer';
import { TextLabel } from '../../atoms/text-label';
import styles from './checkbox.module.scss';

export type CheckboxInputProps = {
  children: ReactElement<typeof Checkbox>;
  className?: string;
  'data-testid'?: string;
  errorMessage?: ReactElement | string;
  footer?: ReactElement | string;
  label: ReactElement | string;
  wide?: boolean;
};

type CheckboxInputComponent = FC<CheckboxInputProps> & {
  Checkbox: typeof CheckboxAtom;
  Footer: typeof FieldFooter;
  Label: typeof TextLabel;
};

/**
 * `Checkbox` is a flexible checkbox component that includes a customizable label and footer.
 * It provides a structured way to combine a checkbox with additional information.
 *
 * @property {ReactElement<typeof Checkbox>} children - The checkbox component itself.
 * @property {string} [className] - Additional CSS classes for the wrapper element.
 * @property {ReactElement<typeof FieldFooter>} [footer] - An optional footer, e.g., for additional information.
 * @property {ReactElement<typeof TextLabel>} label - The label for the checkbox.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label={<Checkbox.Label>Accept Terms & Conditions</Checkbox.Label>}
 *   footer={<Checkbox.Footer>More information</Checkbox.Footer>}
 * >
 *   <Checkbox.Checkbox checked={isChecked} onChange={handleCheckboxChange} />
 * </Checkbox>
 * ```
 */
export const Checkbox: CheckboxInputComponent = ({
  children,
  className,
  'data-testid': dataTestId,
  errorMessage,
  footer,
  label,
  wide = false,
}) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: part of children
  <label className={classNames(styles.label, className)} data-testid={dataTestId}>
    <span className={classNames(styles.wrapper, { [styles.wide]: wide })}>
      {children}
      {<TextLabel>{label}</TextLabel>}
      {Boolean(footer) && (
        <FieldFooter className={styles.footer} data-testid="field-footer">
          {footer}
        </FieldFooter>
      )}
      {Boolean(errorMessage) && (
        <FieldFooter className={styles['error-message']} data-testid="field-error" error icon={<AlertIconXs />}>
          {errorMessage}
        </FieldFooter>
      )}
    </span>
  </label>
);

Checkbox.Label = TextLabel;
Checkbox.Footer = FieldFooter;
Checkbox.Checkbox = CheckboxAtom;
